import { useEffect, useState } from "react";
import { PenSquare, Trash2, Heart, Newspaper, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty, inputCls } from "@/components/kit";
import { api, errMsg, fmtDate } from "@/lib/api";
import { FormDialog, SectionHead, Toggle } from "./common";
import { ImageUpload } from "@/components/ImageUpload";

const BLANK = { title: "", body: "", image_url: "", visibility: "public", poll_question: "", poll_text: "", notify_fans: true };

export const PollBars = ({ poll }) => {
  const total = poll.options.reduce((a, o) => a + o.votes, 0) || 1;
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-white">{poll.question}</p>
      {poll.options.map((o) => (
        <div key={o.text} className="relative overflow-hidden rounded border border-white/10 px-3 py-2 text-sm">
          <div className="absolute inset-y-0 left-0 bg-[#CCFF00]/15" style={{ width: `${(o.votes / total) * 100}%` }} />
          <span className="relative flex justify-between text-slate-200"><span>{o.text}</span><span className="font-mono">{o.votes}</span></span>
        </div>
      ))}
    </div>
  );
};

const PostCard = ({ p, onDelete }) => (
  <Panel data-testid={`post-card-${p.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="flex gap-2">{p.visibility === "members" ? <Pill tone="amber"><Lock className="h-3 w-3" />members only</Pill> : <Pill tone="green">public</Pill>}<Pill><Heart className="h-3 w-3" />{p.likes}</Pill></div>
        <p className="mt-2 font-display text-2xl font-bold uppercase text-white">{p.title}</p>
        <p className="font-mono text-xs text-slate-500">{fmtDate(p.created_at)} · {p.author}</p>
      </div>
      <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => onDelete(p)} data-testid={`delete-post-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
    {p.body && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-300">{p.body}</p>}
    {p.poll && <div className="mt-4"><PollBars poll={p.poll} /></div>}
  </Panel>
);

export default function News({ teamId }) {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const load = () => api.get(`/teams/${teamId}/posts`).then((r) => setPosts(r.data));
  useEffect(() => {
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    const { poll_text, ...rest } = f;
    try {
      await api.post(`/teams/${teamId}/posts`, { ...rest, poll_options: poll_text.split("\n") });
      toast.success(f.notify_fans && f.visibility === "public" ? "Published & fans emailed" : "Published");
      setOpen(false);
      setF(BLANK);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/teams/${teamId}/posts/${p.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <SectionHead title="News & polls" text="Match reports, announcements and fan polls like Player of the Match.">
        <Button className="rounded-full font-bold" onClick={() => setOpen(true)} data-testid="add-post-btn"><PenSquare className="mr-1 h-4 w-4" /> New post</Button>
      </SectionHead>
      {!posts.length && <Empty icon={Newspaper} title="No posts yet" text="Share your first match report." />}
      <div className="grid gap-4 xl:grid-cols-2">{posts.map((p) => <PostCard key={p.id} p={p} onDelete={del} />)}</div>
      <FormDialog open={open} onOpenChange={setOpen} title="New post" onSubmit={create} submitLabel="Publish" testid="post-form">
        <Field label="Headline"><Input required value={f.title} onChange={set("title")} data-testid="post-title-input" /></Field>
        <Field label="Story"><textarea className={`${inputCls} h-28`} value={f.body} onChange={set("body")} data-testid="post-body-input" /></Field>
        <Field label="Photo (optional)"><ImageUpload value={f.image_url} onChange={(v) => setF({ ...f, image_url: v })} teamId={teamId} testid="post-image" wide /></Field>
        <Field label="Who can see it">
          <select className={inputCls} value={f.visibility} onChange={set("visibility")} data-testid="post-visibility-select">
            <option value="public">Everyone (fan hub)</option><option value="members">Squad members only</option>
          </select>
        </Field>
        <Field label="Poll question (optional)"><Input value={f.poll_question} onChange={set("poll_question")} placeholder="Player of the Match?" data-testid="post-poll-question-input" /></Field>
        {f.poll_question && <Field label="Poll options" hint="One per line, at least two"><textarea className={`${inputCls} h-24`} value={f.poll_text} onChange={set("poll_text")} data-testid="post-poll-options-input" /></Field>}
        {f.visibility === "public" && <Toggle checked={f.notify_fans} onChange={(v) => setF({ ...f, notify_fans: v })} label="Email this to all fans" testid="post-notify-fans-toggle" />}
      </FormDialog>
    </div>
  );
}
